import {
  AvailableUnit,
  AvailableUnitExtended,
  Floorplan,
  MonthlyFees,
  Price,
} from "../types/property-types";

export const extendAvailableUnits = ({
  floorplans,
  monthlyFees,
  units,
}: {
  floorplans?: Floorplan[];
  monthlyFees?: MonthlyFees;
  units: AvailableUnit[];
}): AvailableUnitExtended[] => {
  return units?.map((unit) => extendUnit({ floorplans, monthlyFees, unit }));
};

const extendUnit = ({
  floorplans,
  monthlyFees,
  unit,
}: {
  floorplans?: Floorplan[];
  monthlyFees?: MonthlyFees;
  unit: AvailableUnit;
}): AvailableUnitExtended => {
  const extendUnit = unit as AvailableUnitExtended;
  addPrices({ unit: extendUnit });
  addFees({ monthlyFees, unit: extendUnit });
  addFloorPlan({ floorplans, unit: extendUnit });
  return extendUnit;
};

const addPrices = ({ unit }: { unit: AvailableUnitExtended }): void => {
  if (unit.rentMatrix && Object.keys(unit.rentMatrix).length) {
    unit.prices = rentMatrixToArray(unit);
  }
};

const rentMatrixToArray = (unit: AvailableUnit): Price[] => {
  return Object.entries(unit.rentMatrix).map((entry) =>
    rentMatrixEntryToPrice({ startDate: unit.rentMatrixStart, entry }),
  );
};

const rentMatrixEntryToPrice = ({
  startDate,
  entry: [key, value],
}: {
  startDate: string;
  entry: [string, number];
}): Price => {
  const [offset, leaseLength] = key.split("-");
  const leaseStartsOn = new Date(`${startDate}T00:00:00`);
  leaseStartsOn.setDate(leaseStartsOn.getDate() + Number(offset));

  return {
    leaseTerm: `${Number(leaseLength)}`,
    leaseStartsOn: leaseStartsOn.toISOString().split("T")[0],
    price: value.toString(),
  };
};

const addFees = ({
  monthlyFees,
  unit,
}: {
  monthlyFees?: MonthlyFees;
  unit: AvailableUnitExtended;
}): void => {
  const minFee = monthlyFees?.requiredMonthlyFeesMin ?? 0;
  const maxFee = monthlyFees?.requiredMonthlyFeesMax ?? 0;

  if (!unit.prices || unit.prices.length === 0) {
    unit.minBasePrice = unit.minPrice;
    unit.maxBasePrice = unit.maxPrice;
    unit.minPriceMinFee = unit.minPrice + minFee;
    unit.minPriceMaxFee = unit.minPrice + maxFee;
    unit.maxPriceMaxFee = unit.maxPrice + maxFee;
    unit.isFlatPricing = true;
    unit.leaseLength = 12;
    return;
  }

  const preferredPrice = unit.prices.reduce(
    (preferred: Price, current: Price) => {
      if (Number(current.price) < Number(preferred.price)) {
        //If current price is less than preferred, current becomes new preferred
        return current;
      } else if (Number(current.price) === Number(preferred.price)) {
        const preferredDiff = Math.abs(12 - Number(preferred.leaseTerm));
        const currentDiff = Math.abs(12 - Number(current.leaseTerm));
        //If current price is the same as preferred but current lease length is closer to 12 months, current becomes new preferred
        if (currentDiff < preferredDiff) {
          return current;
        } else if (currentDiff === preferredDiff) {
          //If current price is the same as preferred, current lease length the same difference to 12 months and current lease term is longer than preferred, current becomes new preferred
          if (Number(current.leaseTerm) > Number(preferred.leaseTerm)) {
            return current;
          } else {
            return preferred;
          }
        } else {
          return preferred;
        }
      } else {
        return preferred;
      }
    },
    unit.prices[0],
  );

  unit.isFlatPricing = false;
  unit.minPriceMinFee = Number(preferredPrice.price) + minFee;
  unit.minPriceMaxFee = Number(preferredPrice.price) + maxFee;
  unit.maxPriceMaxFee = unit.maxPrice + maxFee;
  unit.minBasePrice = Number(preferredPrice.price);
  unit.maxBasePrice = Number(preferredPrice.price);
  unit.leaseLength = Number(preferredPrice.leaseTerm);
};

const addFloorPlan = ({
  floorplans,
  unit,
}: {
  floorplans?: Floorplan[];
  unit: AvailableUnitExtended;
}): void => {
  if (floorplans) {
    const floorplan = floorplans.find(
      (floorplan) => unit.floorPlanId === floorplan.id,
    );
    if (floorplan) {
      unit.floorplan = floorplan;
    }
  }
};
